import { History } from 'history'
import React from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import TotpGuard from '@/app/component/guard/totp'
import Layout from '@/app/component/layout/page'
import { PAGE } from '@/app/definitions'
import { AuthGuard, AuthHeader } from '@/app/module/auth'
import { CampaignsItem } from '@/app/module/campaigns'
import CampaignFilesProvider from '@/app/module/campaigns/component/campaign-files-provider'
import ChatbotEditor from '@/app/module/campaigns/component/item/chatbot'
import { URLS_TO_TYPE } from '@/app/module/campaigns/definitions'
import { resetDidChange } from '@/app/module/campaigns/store/slices/campaign-item.slice'
import { CampaignTemplateUrl } from '@/app/module/campaigns/types'
import Notifications from '@/app/module/notifications'
import { MatchProps } from '@/app/types'

type Props = {
  history: History
  match: MatchProps<{
    campaignsId: string
    campaignType: string
    step?: string
  }>
}

export const getCampaignFinishPath = (campaignId?: number | string) =>
  campaignId ? `/campaigns/${campaignId}` : '/campaigns?page=1'

function CampaignsItemPage({ match, history }: Props) {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const itemId = match.params.campaignsId
  const type = URLS_TO_TYPE[match.params.campaignType as CampaignTemplateUrl]

  React.useEffect(() => {
    dispatch(resetDidChange())
    return () => {
      dispatch(resetDidChange())
    }
  }, [dispatch])

  React.useEffect(() => {
    if (itemId || type) {
      return
    }
    navigate('/campaigns/create')
  }, [itemId, navigate, type])

  const onFinish = React.useCallback(
    (campaignId?: number | string) => {
      dispatch(resetDidChange())
      setTimeout(() => {
        navigate(getCampaignFinishPath(campaignId))
      }, 100)
    },
    [dispatch, navigate],
  )

  const onSave = React.useCallback(
    ({ id, tab }: { id: number | string; tab?: string }) => {
      dispatch(resetDidChange())
      navigate(`/campaigns/edit/${id}/${tab}`)
    },
    [dispatch, navigate],
  )

  const showCampaignItem = !!(itemId || type)

  return (
    <AuthGuard url={match.url} path={match.path}>
      <Layout disableScrolling title="Campaigns - engageSPARK">
        <AuthHeader currentPage={PAGE.CAMPAIGNS.INDEX} />
        <TotpGuard>
          {showCampaignItem && (
            <>
              {type === 'chatbot' && (
                <CampaignFilesProvider campaignId={itemId}>
                  <ChatbotEditor campaignId={itemId} />
                </CampaignFilesProvider>
              )}
              {type !== 'chatbot' && (
                <CampaignsItem
                  history={history}
                  type={type}
                  itemId={itemId}
                  step={match.params.step}
                  onFinish={onFinish}
                  onSave={onSave}
                />
              )}
            </>
          )}
        </TotpGuard>
        <Notifications />
      </Layout>
    </AuthGuard>
  )
}

export default CampaignsItemPage
